import React, { useCallback, useEffect, useState } from "react";
import ModuleHeader from "../../components/ModuleHeader/ModuleHeader";
import ModuleTable from "../../components/ModuleTable/ModuleTable";
import { getAuditEvent, getAuditMeta, getAuditTrail } from "../../api/adminAuditApi";
import "./audit-trail.scss";

const AUDIT_COLUMNS = ["Date / Time", "Administrator", "Action", "Module", "Activity", "IP Address", ""];
const AUDIT_TEMPLATE = "minmax(120px, 0.85fr) minmax(130px, 0.95fr) minmax(90px, 0.65fr) minmax(100px, 0.7fr) minmax(200px, 1.8fr) minmax(130px, 1.15fr) 36px";



const formatDateTime = (value) => value
  ? new Date(value).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  : "—";

const titleCase = (value) => String(value || "")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const defaultFilters = {
  keyword: "",
  action: "",
  module: "",
  admin_user_id: "",
  date_from: "",
  date_to: "",
  page: 1,
  per_page: 25,
};

const DetailRow = ({ label, value }) => (
  <div className="audit-trail__detail-row">
    <span>{label}</span>
    <strong>{value || "—"}</strong>
  </div>
);

export default function AuditTrailPage() {
  const [filters, setFilters] = useState(defaultFilters);
  const [meta, setMeta] = useState({ actions: [], modules: [], administrators: [] });
  const [result, setResult] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getAuditMeta().then(setMeta).catch((requestError) => setError(requestError.message));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setResult(await getAuditTrail(filters));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const timer = window.setTimeout(load, filters.keyword ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, filters.keyword]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value, page: 1 }));
  };

  const resetFilters = () => setFilters(defaultFilters);

  const openDetail = async (event) => {
    setDetailLoading(true);
    setSelected(event);
    try {
      setSelected(await getAuditEvent(event.id));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="audit-trail">
      <ModuleHeader
        title="Audit Trail"
        description="Review administrator activity and changes across the platform."
        actions={(
          <div className="audit-trail__hero-count">
            <span>Recorded Events</span>
            <strong>{result.total || 0}</strong>
          </div>
        )}
      />

      <section className="audit-trail__filters">
        <label className="audit-trail__field audit-trail__field--search">
          <span>Search</span>
          <div>
            <i className="ti ti-search" aria-hidden="true"></i>
            <input
              value={filters.keyword}
              onChange={(event) => updateFilter("keyword", event.target.value)}
              placeholder="Search administrator, activity, target, or IP..."
            />
          </div>
        </label>
        <label className="audit-trail__field">
          <span>Action</span>
          <select value={filters.action} onChange={(event) => updateFilter("action", event.target.value)}>
            <option value="">All Actions</option>
            {meta.actions.map((action) => <option key={action} value={action}>{titleCase(action)}</option>)}
          </select>
        </label>
        <label className="audit-trail__field">
          <span>Module</span>
          <select value={filters.module} onChange={(event) => updateFilter("module", event.target.value)}>
            <option value="">All Modules</option>
            {meta.modules.map((module) => <option key={module} value={module}>{titleCase(module)}</option>)}
          </select>
        </label>
        <label className="audit-trail__field">
          <span>Administrator</span>
          <select value={filters.admin_user_id} onChange={(event) => updateFilter("admin_user_id", event.target.value)}>
            <option value="">All Administrators</option>
            {meta.administrators.map((admin) => <option key={admin.id} value={admin.id}>{admin.name || admin.email}</option>)}
          </select>
        </label>
        <label className="audit-trail__field">
          <span>From</span>
          <input type="date" value={filters.date_from} onChange={(event) => updateFilter("date_from", event.target.value)} />
        </label>
        <label className="audit-trail__field">
          <span>To</span>
          <input type="date" min={filters.date_from || undefined} value={filters.date_to} onChange={(event) => updateFilter("date_to", event.target.value)} />
        </label>
        <button type="button" className="audit-trail__reset" onClick={resetFilters}>
          <i className="ti ti-filter-off" aria-hidden="true"></i>
          Reset
        </button>
      </section>

      {error ? <div className="audit-trail__error">{error}</div> : null}

      <ModuleTable
        title="Activity Log"
        description="Successful administrative changes are recorded automatically."
        count={`${result.total || 0} event${result.total === 1 ? "" : "s"}`}
        columns={AUDIT_COLUMNS}
        columnTemplate={AUDIT_TEMPLATE}
        variant="audit"
        empty={
          loading ? (
            "Loading audit trail..."
          ) : result.data.length ? null : (
            <>
              <i className="ti ti-history-off" aria-hidden="true"></i>
              <strong>No audit events found</strong>
              <span>Try adjusting the filters or date range.</span>
            </>
          )
        }
        footer={
          result.last_page > 1 ? (
            <ModuleTable.Pagination
              page={result.current_page}
              lastPage={result.last_page}
              onPrev={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
              onNext={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
            />
          ) : null
        }
      >
        {!loading && result.data.map((event) => (
          <ModuleTable.Row
            key={event.id}
            clickable
            columnTemplate={AUDIT_TEMPLATE}
            onClick={() => openDetail(event)}
          >
            <span>{formatDateTime(event.created_at)}</span>
            <span className="module-table__actor">
              <strong>{event.actor_name || "System"}</strong>
              <small>{event.actor_email || "—"}</small>
            </span>
            <span>
              <ModuleTable.Badge tone={event.action}>{titleCase(event.action)}</ModuleTable.Badge>
            </span>
            <span>{titleCase(event.module)}</span>
            <span className="module-table__activity">{event.description}</span>
            <span className="module-table__ip">{event.ip_address || "—"}</span>
            <span className="module-table__view">
              <i className="ti ti-chevron-right" aria-hidden="true"></i>
            </span>
          </ModuleTable.Row>
        ))}
      </ModuleTable>

      {selected ? (
        <div className="audit-trail__overlay" role="dialog" aria-modal="true" aria-label="Audit event details" onClick={() => setSelected(null)}>
          <aside className="audit-trail__drawer" onClick={(event) => event.stopPropagation()}>
            <div className="audit-trail__drawer-head">
              <div><span>Audit Event #{selected.id}</span><h3>{selected.description}</h3></div>
              <button type="button" aria-label="Close details" onClick={() => setSelected(null)}><i className="ti ti-x" aria-hidden="true"></i></button>
            </div>
            {detailLoading ? <div className="audit-trail__drawer-loading">Loading details...</div> : (
              <div className="audit-trail__drawer-body">
                <div className="audit-trail__detail-grid">
                  <DetailRow label="Date / Time" value={formatDateTime(selected.created_at)} />
                  <DetailRow label="Action" value={titleCase(selected.action)} />
                  <DetailRow label="Administrator" value={selected.actor_name || "System"} />
                  <DetailRow label="Email" value={selected.actor_email} />
                  <DetailRow label="Module" value={titleCase(selected.module)} />
                  <DetailRow label="Target" value={selected.subject_label || (selected.subject_id ? `${selected.subject_type} #${selected.subject_id}` : "—")} />
                  <DetailRow label="IP Address" value={selected.ip_address} />
                  <DetailRow label="Result" value={`HTTP ${selected.status_code}`} />
                </div>
                <div className="audit-trail__technical">
                  <h4>Request</h4>
                  <p><strong>{selected.method}</strong> {selected.path}</p>
                </div>
                <div className="audit-trail__changes">
                  <h4>Submitted Changes</h4>
                  {selected.changes && Object.keys(selected.changes).length ? (
                    <dl>
                      {Object.entries(selected.changes).map(([key, value]) => (
                        <div key={key}>
                          <dt>{titleCase(key)}</dt>
                          <dd>{typeof value === "object" ? JSON.stringify(value) : String(value ?? "—")}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : <p>No submitted fields were recorded for this action.</p>}
                </div>
                <div className="audit-trail__user-agent">
                  <h4>Device</h4>
                  <p>{selected.user_agent || "—"}</p>
                </div>
              </div>
            )}
          </aside>
        </div>
      ) : null}
    </div>
  );
}
